import React, { useState } from 'react';
import { Button, Col, Dropdown, Form, Row } from 'react-bootstrap';
import { TriyoForm } from '../../../../../../../components/TriyoForm';
import { FIELD_TYPES } from '../../../../../../../components/DynamicField/Tasks/utils/utils';
import '../admin-dynamic-fields-panel.scss';
import MoveUpMoveDownButtons from './move-up-move-down-buttons';

const defaultField = { Label: '', Required: false };

const newFieldsDropdownItems = [
    {
        iconCSSClass: 'text-icon',
        name: 'Long Text Entry',
        field: { ...defaultField, FieldType: FIELD_TYPES.multiText, Placeholder: 'Enter Comments' },
    },
    {
        iconCSSClass: 'text-icon',
        name: 'Short Text Entry',
        field: {
            ...defaultField,
            FieldType: FIELD_TYPES.singleText,
            Placeholder: 'Enter Comments',
        },
    },
    {
        iconCSSClass: 'date-icon',
        name: 'Date',
        field: { ...defaultField, FieldType: FIELD_TYPES.date, Placeholder: 'DD/MM/YYYY' },
    },
    {
        iconCSSClass: 'clock-icon',
        name: 'Time',
        field: { ...defaultField, FieldType: FIELD_TYPES.time, Placeholder: 'HH:MM' },
    },
    {
        iconCSSClass: 'number-icon',
        name: 'Numeric (Integer)',
        field: { ...defaultField, FieldType: FIELD_TYPES.integer, Placeholder: '###' },
    },
    {
        iconCSSClass: 'number-icon',
        name: 'Numeric (Decimal)',
        field: { ...defaultField, FieldType: FIELD_TYPES.decimal, Placeholder: '#.#' },
    },
    {
        iconCSSClass: 'text-icon',
        name: 'Dropdown',
        field: { ...defaultField, FieldType: FIELD_TYPES.dropDown, Placeholder: 'Enter Text', LookupValues: ["", ""], Lookup: true },
    },
    {
        iconCSSClass: 'checkbox-icon',
        name: 'Checkbox',
        field: { ...defaultField, FieldType: FIELD_TYPES.checkbox, Placeholder: 'Enter Text', LookupValues: ["", ""], Lookup: true },
    },
    {
        iconCSSClass: 'radio-icon',
        name: 'Radio Buttons',
        field: { ...defaultField, FieldType: FIELD_TYPES.radioButtons, Placeholder: 'Enter Text', LookupValues: ["", ""], Lookup: true },
    },
    {
        iconCSSClass: 'text-icon',
        name: 'Table',
        field: { ...defaultField, FieldType: FIELD_TYPES.table, Placeholder: '', Table: { Columns: [] } },
    },
    {
        iconCSSClass: 'toggle-icon',
        name: 'Toggle',
        field: { ...defaultField, FieldType: FIELD_TYPES.toggle, Placeholder: 'Enter Text' },
    },
];

export function getDefaultPlaceholderForFieldType(fieldType) {
    return (
        newFieldsDropdownItems.find(({ field }) => field.FieldType === fieldType)?.field
            .Placeholder ?? ''
    );
}

export function fieldOptionPlaceholder(fieldType) {
    switch (fieldType) {
        case FIELD_TYPES.dropDown: return "Dropdown option";
        case FIELD_TYPES.radioButtons: return "Radio Button option";
        case FIELD_TYPES.checkbox: return "Checkbox Option";
    }
}

const AdminDynamicFieldsTemplate = (props) => {
    const { dynamicFields, setDynamicFields, objectTypes, checked, type } = props;
    const [selectedLock, setSelectedLock] = useState("1");

    const addNewField = (newField = { defaultField }) => {
        const objectType = type === "projectTemplate" ? "Project" : newField.FieldType === FIELD_TYPES.table ? 'TaskDetail' : objectTypes?.[0].type;
        setDynamicFields([...dynamicFields, { ...newField, ObjectType: objectType }]);
    }

    const removeField = (removeIdx) => {
        setDynamicFields(dynamicFields.filter((_, idx) => removeIdx !== idx));
    }

    function handleChange(obj, index) {
        setDynamicFields(
            dynamicFields.map((field, idx) =>
                index === idx ? { ...field, ...obj, touched: true } : field
            )
        );
    }

    const addLookup = (fieldIndex) => {
        setDynamicFields(
            dynamicFields.map((field, idx) =>
                fieldIndex === idx ? { ...field, LookupValues: [...field.LookupValues, ""] } : field
            )
        );
    }

    const switchToDropdown = (fieldIndex) => {
        setDynamicFields(
            dynamicFields.map((field, idx) =>
                fieldIndex === idx ? { ...field, FieldType: FIELD_TYPES.dropDown } : field
            )
        );
    }

    const addTableColumnHeader = (fieldIndex) => {
        setDynamicFields(
            dynamicFields.map((field, idx) => {
                if (fieldIndex === idx) {
                    const newColumn = { Id: 0, Name: '', Order: field.Table.Columns.length + 1, Type: 'String', LockStatus: 1 };
                    return { ...field, Table: { ...field.Table, Columns: [...field.Table.Columns, newColumn] } };
                }

                return field;
            })
        );
    }

    const tableColumnHeaderChange = (value, fieldIndex, columnIndex) => {
        setDynamicFields(
            dynamicFields.map((field, idx) => {
                if (fieldIndex === idx) {
                    const columns = field.Table.Columns.map((column, index) => columnIndex === index ? { ...column, Name: value } : column)
                    return { ...field, Table: { ...field.Table, Columns: columns } };
                }

                return field;
            })
        );
    }

    const tableColumnHeaderChangeType = (type, fieldIndex, columnIndex) => {
        setDynamicFields(
            dynamicFields.map((field, idx) => {
                if (fieldIndex === idx) {
                    const columns = field.Table.Columns.map((column, index) => columnIndex === index ? { ...column, Type: type } : column)
                    return { ...field, Table: { ...field.Table, Columns: columns } };
                }

                return field;
            })
        );
    }

    const tableColumnHeaderChangeLookupValues = (values, fieldIndex, columnIndex) => {
        setDynamicFields(
            dynamicFields.map((field, idx) => {
                if (fieldIndex === idx) {
                    const columns = field.Table.Columns.map((column, index) => columnIndex === index ? { ...column, LookupValues: values } : column)
                    return { ...field, Table: { ...field.Table, Columns: columns } };
                }

                return field;
            })
        );
    }

    const removeColumnHeader = (fieldIndex, columnIndex) => {
        setDynamicFields(
            dynamicFields.map((field, idx) => {
                if (fieldIndex === idx) {
                    return { ...field, Table: { ...field.Table, Columns: field.Table.Columns.filter((_, index) => columnIndex !== index) } };
                }

                return field;
            })
        );
    }

    const handleTableHeaderSwap = (fieldIndex, firstIdx, secondIdx) => {   

        //Animation For row swapping, Id's are unique by appending FieldId + Column ID
        let a = document.getElementById(`dynamicTableDesigner`+fieldIndex+`_`+firstIdx);
        let b = document.getElementById(`dynamicTableDesigner`+fieldIndex+`_`+secondIdx);
        //remove existing classes
        a.classList.remove("moveUp");
        a.classList.remove("moveDown");
        b.classList.remove("moveUp");
        b.classList.remove("moveDown");
        //trigger reflow
        void a.offsetWidth;
        void b.offsetWidth;
        //add animation
        a.classList.add("moveUp");
        b.classList.add("moveDown");

        setDynamicFields(
            dynamicFields.map((field, idx) => {
                if (fieldIndex === idx) {
                    const [first, second] = [field.Table.Columns[firstIdx], field.Table.Columns[secondIdx]];
                    let firstOrder = first.Order;
                    let secondOrder = second.Order;
                    const columns = field.Table.Columns.map((column, index) => {                        
                        if (index === firstIdx){
                            second.Order = firstOrder;
                            return second;
                        } 
                        if (index === secondIdx){
                            first.Order = secondOrder;
                            return first;
                        } 
                        return column;
                    })
                    return { ...field, Table: { ...field.Table, Columns: columns } };
                }
                return field;
            })
        );
    }

    const setLockOptions = (fieldIndex, columnIndex) => {
        setDynamicFields(
            dynamicFields.map((field, idx) => {
                if (fieldIndex === idx) {
                    const columns = field.Table.Columns.map((column, index) => columnIndex === index ? { ...column, LockStatus: selectedLock } : column)
                    return { ...field, Table: { ...field.Table, Columns: columns } };
                }

                return field;
            })
        );
    }

    const removeLookup = (fieldIndex, lookupIndex) => {
        setDynamicFields(
            dynamicFields.map((field, idx) =>
                fieldIndex === idx ? { ...field, LookupValues: field.LookupValues.filter((_, idx) => lookupIndex !== idx) } : field
            )
        );
    }

    const lookupChange = (value, fieldIndex, lookupIndex) => {
        setDynamicFields(
            dynamicFields.map((field, fidx) =>
                fieldIndex === fidx ? { ...field, LookupValues: field.LookupValues.map((lookup, lidx) => lookupIndex === lidx ? value : lookup) } : field
            )
        );
    }

    const handleSwap = (firstIdx, secondIdx) => {
        const [first, second] = [dynamicFields[firstIdx], dynamicFields[secondIdx]];

        setDynamicFields(
            dynamicFields.map((field, idx) => {
                if (idx === firstIdx) return second;
                if (idx === secondIdx) return first;
                return field;
            })
        );
    }

    const addField = () => {
        return (
            <>
                <Dropdown.Toggle className="add-field-btn" variant="outline-primary">
                    <span className="add-field-btn-text">Add {(type === "projectTemplate") ? "New Project " : "New Task "}Field</span>
                    <div className="add-field-icon" />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {newFieldsDropdownItems.map((item) => (
                        (type !== "projectTemplate" || (type === "projectTemplate" && item.field.FieldType !== FIELD_TYPES.table)) &&
                        (<Dropdown.Item
                            className="field-type-btn"
                            onClick={() => addNewField(item.field)}
                        >
                            <div className={`dropdown-icon ${item.iconCSSClass}`}></div>
                            <div className="">{item.name}</div>
                            <span></span>
                        </Dropdown.Item>)
                    ))}
                </Dropdown.Menu>
            </>
        );
    }

    const lockMenu = (fieldIdx, columnIndex, LockStatus) => {
        return (
            <Dropdown drop={"right"} >
                <Dropdown.Toggle onMouseDown={e => {setSelectedLock(LockStatus)}} className={'lockButton btn-icon1 lock ' + (LockStatus == 1 ? "lock-open" : LockStatus == 2 ? "lock-semi-open" : "lock-closed")}
                >
                </Dropdown.Toggle>
                <Dropdown.Menu className='lockDropDown'>
                    <Dropdown.Header className='lockHeader'>
                        Field Permission
                    </Dropdown.Header>
                    <div className="lockItem" >
                        <div className="lockItemName">
                            <input type="checkbox" name="lockStatus" value="1" checked={selectedLock=="1"} onChange={(e) => {setSelectedLock(e.target.value)}} />
                            <div className='lockButton btn-icon1 lock-open noHover'></div>
                            <div>Completely Open (Default)</div>
                        </div>
                        <div className="lockItemDescription">Editors, Approvers or Owners can make changes at any point.</div>
                    </div>
                    <div className="lockItem" >
                        <div className="lockItemName">
                            <input type="checkbox" name="lockStatus" value="2" checked={selectedLock=="2"} onChange={(e) => setSelectedLock(e.target.value)} />
                            <div className='lockButton btn-icon1 lock-semi-open noHover'></div>
                            <div >Semi Open</div>
                        </div>
                        <div className="lockItemDescription">Editors can add new data, but once the Task is Updated, it gets locked. Approvers and Task Owners can make changes at any point.</div>
                    </div>
                    <div className="lockItem">
                        <div className="lockItemName">
                            <input type="checkbox" name="lockStatus" value="3" checked={selectedLock=="3"} onChange={(e) => setSelectedLock(e.target.value)} />
                            <div className='lockButton btn-icon1 lock-closed noHover'></div>
                            <div >Closed</div>
                        </div>
                        <div className="lockItemDescription">Only Approvers or Task Owners can add/change data any point.</div>
                    </div>
                    <Dropdown.Item className="lockSave">
                        <Button onClick={(e) => setLockOptions(fieldIdx, columnIndex)}>Apply Changes</Button>
                    </Dropdown.Item>

                </Dropdown.Menu>
            </Dropdown >
        );
    }

    return (
        <div className="admin-dynamic-fields-template-wrapper">
            {dynamicFields.map((field, fieldIdx) => {
                const isMoveUpDisabled = fieldIdx === 0;
                const isMoveDownDisabled = fieldIdx === dynamicFields.length - 1;
                return (
                    <div className="field-container" key={fieldIdx}>

                        <div className="field-header">
                            {field.FieldType}
                            <div className="field-controls">
                                <Button
                                    className={`btn-icon1 rotate-180 arrow${isMoveUpDisabled ? '-disabled' : ''
                                        }`}
                                    disabled={isMoveUpDisabled}
                                    onClick={() => handleSwap(fieldIdx - 1, fieldIdx)}
                                    type="button"
                                />
                                <Button
                                    className={`btn-icon1 arrow${isMoveDownDisabled ? '-disabled' : ''
                                        }`}
                                    disabled={isMoveDownDisabled}
                                    onClick={() => handleSwap(fieldIdx, fieldIdx + 1)}
                                    type="button"
                                />
                                <Button
                                    className="btn-icon1 delete"
                                    onClick={() => removeField(fieldIdx)}
                                    type="button"
                                />
                            </div>
                        </div>

                        <TriyoForm>
                            <TriyoForm.Input
                                required={true}
                                label="Field Label"
                                value={field.Label}
                                onChange={(e) => handleChange({ Label: e.target.value }, fieldIdx)}
                                type="text"
                                placeholder="Enter Text"
                                isInvalid={field.Label.length < 1 && checked}>

                                {
                                    field.FieldType === FIELD_TYPES.table && <>
                                        <TriyoForm.Label
                                            label="Table Headers"
                                            className="option-label"
                                        />

                                        {field.Table.Columns.map((column, columnIndex) => {
                                            const isColMoveUpDisabled = columnIndex === 0;
                                            const isColMoveDownDisabled = columnIndex === field.Table.Columns.length - 1;
                                            return <>
                                                <Form.Group className="option-input" id={"dynamicTableDesigner"+fieldIdx+"_"+columnIndex}>
                                                    <Form.Label>
                                                        {columnIndex + 1}
                                                    </Form.Label>
                                                    <Form.Control
                                                        value={column.Name}
                                                        className="col-4"
                                                        onChange={(e) => tableColumnHeaderChange(e.target.value, fieldIdx, columnIndex)}
                                                        type="text"
                                                        placeholder="Title"
                                                    />
                                                    <Form.Control
                                                        value={column.Type}
                                                        className="ml-3 col-2"
                                                        onChange={(e) => tableColumnHeaderChangeType(e.target.value, fieldIdx, columnIndex)}
                                                        as="select"
                                                        placeholder="Column Type"
                                                    >
                                                        <option value="String">General</option>
                                                        <option value="Date">Date (YYYY/MM/DD)</option>
                                                        <option value="Numeric">Numeric</option>
                                                        <option value="Lookup">List</option>
                                                    </Form.Control>
                                                    <Button
                                                        className="btn-icon1 delete"
                                                        onClick={() => removeColumnHeader(fieldIdx, columnIndex)}
                                                        type="button"
                                                    />                                                  
                                                    {lockMenu(fieldIdx, columnIndex, column.LockStatus)}
                                                    <Button
                                                        className={`btn-icon1 rotate-180 arrow${isColMoveUpDisabled ? '-disabled' : ''
                                                            }`}
                                                        disabled={isColMoveUpDisabled}
                                                        onClick={() => handleTableHeaderSwap(fieldIdx,columnIndex - 1, columnIndex)}
                                                        type="button"
                                                    />
                                                    <Button
                                                        className={`btn-icon1 arrow${isColMoveDownDisabled ? '-disabled' : ''
                                                            }`}
                                                        disabled={isColMoveDownDisabled}
                                                        onClick={() => handleTableHeaderSwap(fieldIdx,columnIndex, columnIndex + 1)}
                                                        type="button"
                                                    />
                                                </Form.Group>
                                                {column.Type === "Lookup" && <Form.Group className="options-input pl-5">
                                                    <Form.Label>Options <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        value={column.LookupValues}
                                                        className="w-50"
                                                        onChange={(e) => tableColumnHeaderChangeLookupValues(e.target.value, fieldIdx, columnIndex)}
                                                        type="text"
                                                        placeholder="e.g. Option 1, Option 2, Option 3 ..."
                                                    />
                                                </Form.Group>}
                                            </>
                                        })}

                                        <Form.Group className="option-input">
                                            <Form.Label>
                                                {field.Table.Columns.length + 1}
                                            </Form.Label>
                                            <Button onClick={() => addTableColumnHeader(fieldIdx)}>
                                                Add header item
                                            </Button>
                                        </Form.Group>
                                    </>
                                }

                                {
                                    (field.FieldType === FIELD_TYPES.dropDown || field.FieldType === FIELD_TYPES.radioButtons || field.FieldType === FIELD_TYPES.checkbox) && <>
                                        <TriyoForm.Label
                                            label="Options"
                                            className="option-label"
                                        />

                                        {(field.LookupValues.length < 2) && (field.LookupValues = ["", ""])}

                                        {field.Lookup = true}

                                        {field.LookupValues.map((lookup, lookupIndex) => {
                                            return <Form.Group className="option-input">
                                                <Form.Label>
                                                    {lookupIndex + 1}
                                                </Form.Label>
                                                <TriyoForm.Input
                                                    value={lookup}
                                                    onChange={(e) => lookupChange(e.target.value, fieldIdx, lookupIndex)}
                                                    type="text"
                                                    isInvalid={lookupIndex < 2 && lookup.length < 1 && checked}
                                                    placeholder={fieldOptionPlaceholder(field.FieldType)}
                                                    hideLabel={true}
                                                />
                                                {
                                                    lookupIndex > 1 ? 
                                                        <Button
                                                            className="btn-icon1 delete"
                                                            onClick={() => removeLookup(fieldIdx, lookupIndex)}
                                                            type="button"
                                                        />
                                                        :
                                                        <Button
                                                            className="btn-icon1 no-delete"
                                                            disabled
                                                            type="button"
                                                        />
                                                }
                                                <MoveUpMoveDownButtons setDynamicFields = {setDynamicFields} fieldIdx={fieldIdx} lookupIndex={lookupIndex} lookupLength={field.LookupValues.length} />
                                            </Form.Group>
                                        })}

                                        <Form.Group className="option-input">
                                            <Form.Label>
                                                {field.LookupValues.length + 1}
                                            </Form.Label>
                                            {
                                                field.FieldType === FIELD_TYPES.radioButtons && field.LookupValues?.length > 4 ?
                                                    <Button onClick={() => switchToDropdown(fieldIdx)}>
                                                        Switch to Dropdown
                                                    </Button>
                                                    :
                                                    <Button onClick={() => addLookup(fieldIdx)}>
                                                        Add option
                                                    </Button>
                                            }
                                        </Form.Group>
                                    </>
                                }

                            </TriyoForm.Input>
                        </TriyoForm>

                        <div className="field-footer">
                            <Row className="">
                                <Col xs={3}>
                                    <TriyoForm.Toggle
                                        className="heading-checkbox"
                                        checked={field.Required}
                                        onChange={() =>
                                            handleChange({ Required: !field.Required }, fieldIdx)
                                        }
                                        disabled={field.FieldType === FIELD_TYPES.checkbox || field.FieldType === FIELD_TYPES.toggle}
                                        label="Mandatory Field"
                                    />

                                </Col>
                                {type !== "projectTemplate" &&
                                    <Col>
                                        <Form.Label className="object-type-label">Add to:</Form.Label>
                                        {objectTypes.map((objectType, checkIdx) => {
                                            return (
                                                <Form.Check
                                                    className="project-reminder-type-checkbox"
                                                    inline
                                                    label={objectType.name}
                                                    type="checkbox"
                                                    disabled={objectType.type === 'TaskComment' && field.FieldType === FIELD_TYPES.table}
                                                    id={`${objectType.name
                                                        .toLowerCase()
                                                        .split(' ')
                                                        .join('-')}-${fieldIdx}`}
                                                    key={checkIdx}
                                                    defaultChecked={
                                                        field.ObjectType === objectType.type
                                                    }
                                                    checked={field.ObjectType === objectType.type}
                                                    onChange={() => {
                                                        handleChange(
                                                            { ObjectType: objectType.type },
                                                            fieldIdx
                                                        );
                                                    }}
                                                />
                                            );
                                        })}
                                    </Col>
                                }
                            </Row>
                        </div>
                    </div>
                );
            })}
            {
                (dynamicFields.length === 0) ? (
                    <div className="add-field-btn-container">
                        <div className="add-field-title">Configure {type === "projectTemplate" ? "Project" : "Task"} Fields</div>
                        {(type === "projectTemplate") ?
                            <div className="add-field-btn-background">
                                <p>Add Project Fields to enable the capture and display of additional context around Projects created using this Template.</p>
                                <p>To get started with Project Fields, click the <strong>Add New Project Field</strong> button and select an input type from the dropdown menu.</p>
                            </div>
                            :
                            <div className="add-field-btn-background">
                                <p>Add Task Fields to allow Task Members to capture data or display additional context around Tasks created using this Template.</p>
                                <p>To get started with Task Fields, click the <strong>Add New Task Field</strong> button and select an input type from the dropdown menu.</p>
                            </div>
                        }
                        <Dropdown>
                            {addField()}
                        </Dropdown>
                    </div>
                ) : (
                    <Dropdown>
                        {addField()}
                    </Dropdown>
                )
            }
        </div>
    );
};

export default AdminDynamicFieldsTemplate;
